import { useMemo, useState } from 'react';
import { Check, Clock, Loader2, Lock, Microscope, Network, Zap } from 'lucide-react';
import {
  useDevelopmentDefinitions,
  useDevelopmentProjects,
  useStartProject,
  useCancelProject,
  useForecast,
} from '../../hooks/useDevelopment.js';
import { useProjectCountdown } from '../../hooks/useCountdown.js';
import type { DevelopmentDefinition, DevelopmentProject } from '../../api/development.js';
import ForecastModal from '../development/ForecastModal.js';
import { TECH_BRANCHES } from './techTreeData.js';

interface TechTreePanelProps {
  currentBalance: number;
  stats?: Record<string, number>;
}

function ResearchProgress({ project, dayDurationMs }: { project: DevelopmentProject; dayDurationMs?: number }) {
  const countdown = useProjectCountdown(project.started_at, project.total_construction_days, dayDurationMs);
  return (
    <div className="mt-1.5">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${countdown.isComplete ? 'bg-green-500' : 'bg-cyan-500'}`}
            style={{ width: `${Math.min(100, countdown.progressPercent)}%` }}
          />
        </div>
        <span className="text-[10px] text-slate-400 font-mono whitespace-nowrap">
          {countdown.isComplete ? 'Done' : countdown.formatted}
        </span>
      </div>
      <p className="mt-1 text-[10px] text-cyan-300/80">Researching... effects apply daily while work continues.</p>
    </div>
  );
}

function TechNodeCard({
  name,
  def,
  project,
  isNext,
  currentBalance,
  availableSlots,
  dayDurationMs,
  onForecast,
  onStart,
  onCancel,
  startPending,
  cancelPending,
}: {
  name: string;
  def: DevelopmentDefinition | undefined;
  project: DevelopmentProject | undefined;
  isNext: boolean;
  currentBalance: number;
  availableSlots: number;
  dayDurationMs?: number;
  onForecast: (def: DevelopmentDefinition) => void;
  onStart: (id: string) => void;
  onCancel: (id: string) => void;
  startPending: boolean;
  cancelPending: boolean;
}) {
  if (!def) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-600">
        {name}
      </div>
    );
  }

  const isResearching = !!project && (project.status === 'constructing' || project.status === 'active');
  const isCompleted = def.purchased && !isResearching;
  const canAfford = currentBalance >= def.cost;
  const isAvailable = def.unlocked && !def.purchased && !isResearching;
  const slotsFree = availableSlots > 0;
  const canStart = isAvailable && canAfford && slotsFree && !def.hasConflict;
  const canCancel = isResearching && project?.status === 'constructing' && project.progress_days <= 0;

  let stateClasses = 'bg-slate-900/50 border-slate-800 opacity-70';
  if (isCompleted) stateClasses = 'bg-green-950/30 border-green-800/50';
  else if (isResearching) stateClasses = 'bg-cyan-950/30 border-cyan-700/50';
  else if (isAvailable) stateClasses = isNext ? 'bg-slate-800/60 border-cyan-500/60 ring-1 ring-cyan-500/30' : 'bg-slate-800/50 border-slate-700';

  return (
    <div className={`rounded-lg border p-3 transition-colors ${stateClasses}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {!def.unlocked && !isCompleted && !isResearching && <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
          {isCompleted && <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />}
          {isResearching && <Microscope className="w-3.5 h-3.5 text-cyan-400 shrink-0 animate-pulse" />}
          <span className="text-xs font-semibold truncate">{def.name}</span>
          {isNext && isAvailable && (
            <span className="shrink-0 rounded-full bg-cyan-600/30 border border-cyan-500/50 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-cyan-300">
              Next
            </span>
          )}
        </div>
        {!isCompleted && !isResearching && (
          <span className={`text-[11px] font-mono shrink-0 ${canAfford ? 'text-yellow-400' : 'text-slate-500'}`}>
            {def.cost} cr
          </span>
        )}
        {isCompleted && <span className="text-[10px] text-green-400 shrink-0">Researched</span>}
      </div>
      <p className="text-[11px] text-slate-400 mt-1 leading-snug">{def.description}</p>

      {!isCompleted && (
        <div className="mt-1.5 flex items-center gap-3 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{def.construction_days}d research</span>
          {def.upkeep_cost > 0 && (
            <span className="flex items-center gap-1"><Zap className="w-3 h-3 text-yellow-500" />{def.upkeep_cost} cr/day upkeep</span>
          )}
        </div>
      )}

      {isResearching && project && <ResearchProgress project={project} dayDurationMs={dayDurationMs} />}

      {!def.unlocked && !isCompleted && !isResearching && def.unlockReason && (
        <p className="text-[10px] text-amber-400 mt-1.5">Locked: {def.unlockReason}</p>
      )}

      {isAvailable && !canStart && (
        <p className="text-[10px] text-amber-400 mt-1.5">
          {!canAfford
            ? `Need ${def.cost - currentBalance} more credits`
            : !slotsFree
            ? 'All research slots busy — finish or cancel a project first'
            : 'Conflicts with a branch you already chose'}
        </p>
      )}

      {isAvailable && (
        <div className="flex gap-1.5 mt-2">
          <button
            onClick={() => onForecast(def)}
            className="flex-1 min-h-9 text-[11px] px-2 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-cyan-300 transition-colors"
            style={{ touchAction: 'manipulation' }}
          >
            Forecast
          </button>
          <button
            onClick={() => onStart(def.upgrade_definition_id)}
            disabled={!canStart || startPending}
            className="flex-1 min-h-9 text-[11px] px-2 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white font-medium transition-colors disabled:opacity-50"
            style={{ touchAction: 'manipulation' }}
          >
            {startPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Research'}
          </button>
        </div>
      )}

      {canCancel && (
        <button
          onClick={() => project && onCancel(project.project_id)}
          disabled={cancelPending}
          className="mt-2 w-full min-h-9 text-[11px] px-2 py-1.5 rounded-md text-red-400 hover:text-red-300 hover:bg-red-950/30 transition-colors disabled:opacity-50"
          style={{ touchAction: 'manipulation' }}
        >
          Cancel (50% refund)
        </button>
      )}
    </div>
  );
}

export default function TechTreePanel({ currentBalance, stats }: TechTreePanelProps) {
  const [selectedUpgrade, setSelectedUpgrade] = useState<DevelopmentDefinition | null>(null);
  const [activeBranchId, setActiveBranchId] = useState(TECH_BRANCHES[0].id);
  const { data: defsData, isLoading } = useDevelopmentDefinitions();
  const { data: projectsData } = useDevelopmentProjects();
  const startProject = useStartProject();
  const cancelProject = useCancelProject();
  const { data: forecastData } = useForecast(selectedUpgrade?.upgrade_definition_id || null, 7);

  const definitions = defsData?.data || [];
  const activeProjects = projectsData?.data?.active || [];
  const maxSlots = projectsData?.data?.maxSlots ?? 3;
  const availableSlots = projectsData?.data?.availableSlots ?? 0;
  const dayDurationMs = projectsData?.data?.dayDurationMs;

  const defsByName = useMemo(() => new Map(definitions.map((d) => [d.name, d])), [definitions]);
  const projectsByDefId = useMemo(() => new Map(activeProjects.map((p) => [p.upgrade_definition_id, p])), [activeProjects]);

  // Per-branch summary: completion count and the next researchable node.
  const branchInfo = useMemo(() => {
    return TECH_BRANCHES.map((branch) => {
      let completed = 0;
      let researching = 0;
      let nextName: string | null = null;
      for (const nodeName of branch.nodes) {
        const def = defsByName.get(nodeName);
        if (!def) continue;
        const project = projectsByDefId.get(def.upgrade_definition_id);
        const isResearching = !!project && (project.status === 'constructing' || project.status === 'active');
        if (isResearching) researching += 1;
        else if (def.purchased) completed += 1;
        else if (!nextName && def.unlocked) nextName = nodeName;
      }
      return { ...branch, completed, researching, nextName, total: branch.nodes.length };
    });
  }, [defsByName, projectsByDefId]);

  const technology = stats?.technology || 0;
  const researchSpeed = stats?.researchSpeed || 0;
  const scanRange = stats?.scanRange || 0;
  const population = stats?.population || 0;
  const biodiversity = stats?.biodiversity || 0;
  const energy = stats?.energy || 0;
  const researchOutput = 1 + population / 200 + biodiversity / 300 + energy / 300;

  const handleStart = (upgradeId: string) => {
    if (startProject.isPending) return;
    startProject.mutate(upgradeId);
    setSelectedUpgrade(null);
  };

  const handleCancel = (projectId: string) => {
    if (cancelProject.isPending) return;
    cancelProject.mutate(projectId);
  };

  return (
    <div className="flex flex-col h-full" style={{ touchAction: 'pan-y' }}>
      <div className="p-3 border-b border-slate-800 shrink-0 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Network className="w-5 h-5 text-cyan-400" />
            Tech Tree
          </h2>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${availableSlots > 0 ? 'bg-emerald-950/50 border-emerald-800/40 text-emerald-300' : 'bg-amber-950/50 border-amber-800/40 text-amber-300'}`}>
            {availableSlots}/{maxSlots} research slots free
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[11px]">
          <span className="px-2 py-0.5 rounded-full bg-violet-950/50 border border-violet-800/40 text-violet-300">
            Technology {Math.round(technology)}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-indigo-950/50 border border-indigo-800/40 text-indigo-300">
            Research {Math.round(researchSpeed)}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-sky-950/50 border border-sky-800/40 text-sky-300">
            Sky Map {Math.round(scanRange)}
          </span>
          <span
            className="px-2 py-0.5 rounded-full bg-cyan-950/50 border border-cyan-800/40 text-cyan-300"
            title="Research output multiplier from population, biodiversity, and energy"
          >
            Output x{researchOutput.toFixed(2)}
          </span>
        </div>

        {/* Mobile: one branch at a time via tabs */}
        <div className="flex gap-1 overflow-x-auto pb-0.5 md:hidden" style={{ touchAction: 'pan-x' }}>
          {branchInfo.map((branch) => (
            <button
              key={branch.id}
              onClick={() => setActiveBranchId(branch.id)}
              className={`flex min-h-10 shrink-0 flex-col items-center justify-center rounded-lg px-3 py-1 text-xs transition-colors ${
                activeBranchId === branch.id ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-slate-400'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              <span className="font-medium">{branch.label}</span>
              <span className={`text-[10px] ${activeBranchId === branch.id ? 'text-cyan-100' : 'text-slate-500'}`}>
                {branch.completed}/{branch.total}{branch.researching > 0 ? ' · researching' : ''}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 md:p-4" style={{ touchAction: 'pan-y' }}>
        {isLoading ? (
          <div className="text-slate-400 text-sm">Loading tech tree...</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {branchInfo.map((branch) => (
              <div key={branch.id} className={branch.id === activeBranchId ? 'block' : 'hidden md:block'}>
                <div className="mb-2">
                  <h3 className="flex items-center justify-between text-sm font-semibold text-slate-300">
                    {branch.label}
                    <span className="text-[10px] font-normal text-slate-500">{branch.completed}/{branch.total} researched</span>
                  </h3>
                  <div className="mt-1 h-1 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-cyan-600 transition-all"
                      style={{ width: `${Math.round((branch.completed / Math.max(1, branch.total)) * 100)}%` }}
                    />
                  </div>
                </div>
                <div>
                  {branch.nodes.map((nodeName, index) => {
                    const def = defsByName.get(nodeName);
                    const project = def ? projectsByDefId.get(def.upgrade_definition_id) : undefined;
                    return (
                      <div key={nodeName}>
                        {index > 0 && <div className="ml-5 h-3 w-0.5 bg-slate-700" />}
                        <TechNodeCard
                          name={nodeName}
                          def={def}
                          project={project}
                          isNext={branch.nextName === nodeName}
                          currentBalance={currentBalance}
                          availableSlots={availableSlots}
                          dayDurationMs={dayDurationMs}
                          onForecast={setSelectedUpgrade}
                          onStart={handleStart}
                          onCancel={handleCancel}
                          startPending={startProject.isPending}
                          cancelPending={cancelProject.isPending}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedUpgrade && (
        <ForecastModal
          upgrade={selectedUpgrade}
          forecast={forecastData?.data}
          currentBalance={currentBalance}
          onStart={() => handleStart(selectedUpgrade.upgrade_definition_id)}
          onClose={() => setSelectedUpgrade(null)}
          isStarting={startProject.isPending}
        />
      )}
    </div>
  );
}
